import { useCallback, useMemo, useState } from "react";

/**
 * Lightweight multi-row selection for tables/card grids.
 * Pass the current list of ids; stale selections (ids no longer present) are
 * automatically dropped so the selection self-heals after reloads/deletes.
 */
export function useRowSelection(ids = []) {
  const [set, setSet] = useState(() => new Set());

  const toggle = useCallback((id) => {
    setSet((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const clear = useCallback(() => setSet(new Set()), []);

  const toggleAll = useCallback(() => {
    setSet((prev) => {
      const allSel = ids.length > 0 && ids.every((i) => prev.has(i));
      return allSel ? new Set() : new Set(ids);
    });
  }, [ids]);

  const isSelected = useCallback((id) => set.has(id), [set]);
  const selected = useMemo(() => ids.filter((i) => set.has(i)), [ids, set]);
  const allSelected = ids.length > 0 && selected.length === ids.length;
  const someSelected = selected.length > 0 && !allSelected;

  return { selected, count: selected.length, toggle, toggleAll, clear, isSelected, allSelected, someSelected };
}
